<script setup>
import { ref, computed, onMounted } from 'vue'
import { debounce } from 'quasar'

import { getI18n, getLocalizedEnumItems } from '@/plugins/i18n'
import { useDetectDevice } from '@/composables/device'
import { useNotify } from '@/composables/notify'
import { usePdfPreview } from '@/composables/pdf-preview'
import { useStepper } from '@/composables/stepper'
import { useReportColumnTemplates, usePrintSettings } from '@/composables/report-column-template'
import { useAccountingStore } from '@/stores/accounting.store'
import { usePortalStore, infoBoxIdEnum } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import InfoBox from '@/components/shared/InfoBox.vue'
import PdfPreviewPanel from '@/components/shared/PdfPreviewPanel.vue'
import PrintDialogLayout from '@/components/shared/PrintDialogLayout.vue'
import PrintConfigurator from '@/components/accounting/PrintConfigurator.vue'
import PrintContentEditor from '@/components/accounting/PrintContentEditor.vue'
import StepperNavigation from '@/components/shared/StepperNavigation.vue'

const props = defineProps({
  report: {
    type: Object,
    required: false,
    default: null,
  },
  params: {
    type: Object,
    required: false,
    default: null,
  },
  reportIds: {
    type: Array,
    required: false,
    default: () => [],
  },
  startStep: {
    type: Number,
    required: false,
    default: 0,
  },
})

const emit = defineEmits(['saved', 'cancel'])

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const { notifySuccess } = useNotify()
const accountingStore = useAccountingStore()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const { reportOrientationEnum, reportGroupByEnum, reportDurationFormatEnum, reportExcelSheetEnum, printDocumentTypeEnum } = accountingStore
const { columnCount, isConfidential: isConfidentialConfig, partsOf, enabledParts } = useReportColumnTemplates()

const STEP = accountingStore.reportFileStepEnum
const isExpenseClaim = !!props.report?.expense_claim
const isMultiple = props.reportIds.length > 1
const fixedDocumentType = (() => {
  if (isExpenseClaim) return accountingStore.reportTypeEnum.STAFF_ACTIVITY
  if (props.params?.project_ids?.length) return accountingStore.reportTypeEnum.PROJECT_COST_REPORT
  return null
})()
const { step } = useStepper({ step: props.startStep })

const subject = props.report?.subject ?? props.params?.subject
const catalog = computed(() => accountingStore.reportColumns)
const documentTypes = computed(() => (fixedDocumentType !== null ? [fixedDocumentType] : catalog.value.document_types?.[subject] || []))
const reportTypes = accountingStore.getLocalizedReportTypes()
const reportPrintTemplateId = props.report?.print_template ?? webSiteStore.getDefaultReportPrintTemplate()

const config = ref(null)
const templateId = ref(null)
const printTemplateId = ref(reportPrintTemplateId)
const language = ref(props.report?.language || portalStore.selectedLanguage)
const canRemember = !!props.report && !isExpenseClaim
const remember = ref(canRemember)
const isExporting = ref(false)
const settings = usePrintSettings({ config, templateId, printTemplateId, defaultPrintTemplateId: reportPrintTemplateId })

const enumOptions = (enumObject, labelPrefix) => getLocalizedEnumItems(enumObject, labelPrefix).map(item => ({ label: item.name, value: item.id }))
const orientationOptions = enumOptions(reportOrientationEnum, 'common.report.orientations')
const groupByOptions = enumOptions(reportGroupByEnum, 'common.report.groupBy')
const sheetOptions = enumOptions(reportExcelSheetEnum, 'common.report.sheets')

const isConfidential = computed(() => !!config.value && isConfidentialConfig(config.value))
const typeName = computed(() => {
  if (!config.value) return ''
  const parts = enabledParts(config.value)
  if (parts.length === 1 && partsOf(config.value.document_type).length > 1) return i18n.t(parts[0].title)
  return reportTypes.find(type => type.id === config.value.document_type)?.name || ''
})

const contentOptions = computed(() => (config.value ? [
  { key: 'total_row', label: i18n.t('common.report.totalRow'), value: config.value.total_row },
  ...(isExpenseClaim ? [
    { key: 'signature', label: i18n.t('common.report.signatureBlock'), value: config.value.signature, disable: isConfidential.value },
    { key: 'receipts', label: i18n.t('common.report.receipts'), value: config.value.receipts },
  ] : [
    { key: 'footnotes', label: i18n.t('common.report.footnotes'), value: config.value.footnotes },
  ]),
] : []))

const requestData = () => ({
  ...(props.report ? { report: props.report.id } : props.params),
  ...(isMultiple && { report_ids: props.reportIds }),
  output_config: config.value,
  language: language.value,
  print_template: printTemplateId.value,
})

const previewFileNames = new Map()
const loadPreviewAsync = async() => {
  const preview = await accountingStore.previewReportAsync(requestData())
  previewFileNames.set(preview.url, preview.fileName)
  return preview.url
}
const { blobUrl, source, isLoading, isPrinting, refreshAsync, printAsync } = usePdfPreview(loadPreviewAsync)
const pdfFileName = computed(() => previewFileNames.get(blobUrl.value) ?? '')
const fileName = computed(() => pdfFileName.value.replace(/\.pdf$/i, ''))
const refreshPreview = debounce(refreshAsync, webSiteStore.getInputDebounce())

const onConfigChanged = (value) => {
  config.value = value
  refreshPreview()
}
const onPrintConfigChanged = (field, value) => onConfigChanged({ ...config.value, [field]: value })
const onPrintTemplateChange = (id) => {
  printTemplateId.value = id
  refreshPreview()
}
const onLanguageChange = (value) => {
  language.value = value
  refreshPreview()
}

const rememberAsync = async() => {
  if (!canRemember || !remember.value) return
  await accountingStore.upsertReportAsync({
    id: props.report.id, output_config: config.value, column_template: templateId.value, print_template: printTemplateId.value, language: language.value,
  })
  emit('saved')
}

const onPrintAsync = async() => {
  if (!await printAsync(pdfFileName.value, typeName.value)) return
  await rememberAsync()
  notifySuccess(i18n.t('common.documentPrinted'))
  emit('cancel')
}

const onExportAsync = async() => {
  isExporting.value = true
  await accountingStore.downloadReportExcelAsync(requestData()).finally(() => { isExporting.value = false })
  await rememberAsync()
  notifySuccess(i18n.t('common.documentExported'))
  emit('cancel')
}

onMounted(async() => {
  await Promise.all([accountingStore.fetchReportColumnsAsync(), accountingStore.ensureDocumentPrintTemplatesAsync(), accountingStore.fetchReportColumnTemplatesAsync()])
  const stored = props.report?.output_config ?? props.params?.output_config
  const documentType = documentTypes.value.includes(stored?.document_type) ? stored.document_type : documentTypes.value[0]
  settings.restore(documentType, stored, props.report?.column_template)
  if (!stored?.sections) printTemplateId.value = settings.template.value.print_template ?? reportPrintTemplateId
  refreshAsync()
})
</script>

<template>
  <print-dialog-layout
    :title="`${i18n.t('common.print')} - ${isMultiple ? `${i18n.t('common.expenseClaims')} (${props.reportIds.length})` : props.report?.name || typeName}`"
    :source="source"
    :loading="isLoading"
    :printable="!!blobUrl"
    :printing="isPrinting"
    @cancel="emit('cancel')"
    @print="onPrintAsync"
  >
    <template #options>
      <div v-if="config" class="q-pa-md">
        <q-stepper
          v-model="step"
          vertical
          animated
          header-nav
          class="q-py-none shadow-0"
          style="background: transparent;"
        >
          <q-step
            :name="STEP.DOCUMENT"
            :title="i18n.t('common.report.steps.content')"
            :caption="isExpenseClaim ? i18n.t('common.report.columnsCount', columnCount(config)) : settings.caption.value"
            icon="mdi-file-document-outline"
            :done="step > STEP.DOCUMENT"
          >
            <print-content-editor
              :model-value="config"
              :settings="isExpenseClaim ? null : settings"
              :options="contentOptions"
              :hide-sections="isExpenseClaim"
              :type-name="typeName"
              @update:model-value="onConfigChanged"
              @update:option="onPrintConfigChanged"
              @applied="refreshPreview"
            />
            <info-box v-if="isConfidential" class="q-mt-sm" :message="i18n.t('common.report.confidentialInfo')" />
            <info-box :id="infoBoxIdEnum.REPORT_COLUMNS" class="q-mt-md" :message="i18n.t('common.report.columnsInfo')" />
            <stepper-navigation class="q-mt-md" :continue="() => { step = STEP.PRINT }" />
          </q-step>

          <q-step
            :name="STEP.PRINT"
            :title="i18n.t('common.printConfigurator')"
            icon="mdi-printer-settings"
            :done="step > STEP.PRINT"
          >
            <print-configurator
              :document-type="printDocumentTypeEnum.REPORT"
              :print-template="printTemplateId"
              :language="language"
              @update:print-template="onPrintTemplateChange"
              @update:language="onLanguageChange"
            >
              <div>
                <div class="text-caption text-grey-7 q-mb-xs">{{ i18n.t('common.report.orientation') }}</div>
                <q-btn-toggle
                  :model-value="config.orientation"
                  no-caps
                  unelevated
                  dense
                  toggle-color="primary"
                  :options="orientationOptions"
                  @update:model-value="value => onPrintConfigChanged('orientation', value)"
                />
              </div>
              <div>
                <div class="text-caption text-grey-7 q-mb-xs">{{ i18n.t('common.report.groupRowsBy') }}</div>
                <q-btn-toggle
                  :model-value="config.group_by"
                  no-caps
                  unelevated
                  dense
                  toggle-color="primary"
                  :options="groupByOptions"
                  @update:model-value="value => onPrintConfigChanged('group_by', value)"
                />
              </div>
            </print-configurator>
            <stepper-navigation class="q-mt-md" :back="() => { step = STEP.DOCUMENT }" :continue="isMultiple ? null : () => { step = STEP.EXCEL }" />
          </q-step>

          <q-step
            v-if="!isMultiple"
            :name="STEP.EXCEL"
            :title="i18n.t('common.report.steps.excel')"
            icon="mdi-microsoft-excel"
            :done="false"
          >
            <div class="text-caption text-grey-7 q-mb-xs">{{ i18n.t('common.report.excelSheets') }}</div>
            <q-option-group
              :model-value="config.excel_sheets"
              type="checkbox"
              :options="sheetOptions"
              @update:model-value="value => { config.excel_sheets = value }"
            />
            <q-toggle
              v-model="config.duration_format"
              class="q-mt-sm"
              :true-value="reportDurationFormatEnum.DECIMAL"
              :false-value="reportDurationFormatEnum.HOURS_MINUTES"
              :label="i18n.t('common.report.decimalDurations')"
            />
            <q-input
              :model-value="fileName"
              class="q-mt-sm"
              :label="i18n.t('common.report.fileName')"
              readonly
              suffix=".pdf / .xlsx"
            />
            <q-toggle
              v-if="canRemember"
              v-model="remember"
              class="q-mt-sm"
              :label="i18n.t('common.report.rememberSettings')"
            />
            <stepper-navigation class="q-mt-md" :back="() => { step = STEP.PRINT }" />
          </q-step>
        </q-stepper>
        <div v-if="isMobile" class="q-mt-md" style="height: 60vh;">
          <pdf-preview-panel :source="source" :loading="isLoading" />
        </div>
      </div>
    </template>
    <template v-if="!isMultiple" #actions>
      <q-btn
        class="large-btn"
        color="positive"
        icon="mdi-microsoft-excel"
        :label="i18n.t('common.exportToExcel')"
        :loading="isExporting"
        @click="onExportAsync"
      />
    </template>
  </print-dialog-layout>
</template>

